// Canvas particle engine for the Command Center god-mode effects.
// Everything is drawn procedurally (glowing shapes, sparks, gravity) so the
// effects look like a real animated universe — never flying emoji pictures.

const RAINBOW = ["#ff3b30", "#ff9500", "#ffd60a", "#34c759", "#00c7be", "#0a84ff", "#bf5af2", "#ff375f"];
const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[(Math.random() * arr.length) | 0];

function drawStar(ctx, x, y, r, rot, color, glow) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.shadowColor = glow;
  ctx.shadowBlur = r * 1.6;
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const a = (Math.PI / 2) * -1 + (i * 2 * Math.PI) / 5;
    ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    const a2 = a + Math.PI / 5;
    ctx.lineTo(Math.cos(a2) * r * 0.45, Math.sin(a2) * r * 0.45);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawHeart(ctx, x, y, s, color) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s / 16, s / 16);
  ctx.shadowColor = color;
  ctx.shadowBlur = 12;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, 5);
  ctx.bezierCurveTo(-8, -4, -14, 4, 0, 14);
  ctx.bezierCurveTo(14, 4, 8, -4, 0, 5);
  ctx.fill();
  ctx.restore();
}

function drawFire(ctx, x, y, r) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, "rgba(255,255,220,0.95)");
  g.addColorStop(0.35, "rgba(255,180,40,0.9)");
  g.addColorStop(0.7, "rgba(255,80,10,0.75)");
  g.addColorStop(1, "rgba(180,20,0,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, 6.2832);
  ctx.fill();
}

// Start an effect on a canvas. Returns a stop() cleanup function.
export function runEffect(canvas, effect, duration = 4200) {
  const ctx = canvas.getContext("2d");
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let W = 0, H = 0;
  const resize = () => {
    W = canvas.clientWidth || window.innerWidth;
    H = canvas.clientHeight || window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  window.addEventListener("resize", resize);

  const P = [];
  const start = performance.now();
  let last = start;
  let raf = 0;
  let stopped = false;
  let fireworkAt = start + 120;

  const spawners = {
    "star-rain": () => P.push({ k: "star", x: rand(0, W), y: rand(-40, -4), vx: rand(-14, 14), vy: rand(240, 460), r: rand(6, 15), rot: rand(0, 6.28), vr: rand(-3, 3), tw: rand(0, 6.28), gold: Math.random() < 0.8 }),
    "snow": () => P.push({ k: "snow", x: rand(0, W), y: rand(-30, -4), vx: rand(-25, 25), vy: rand(70, 150), r: rand(2, 6), sw: rand(0, 6.28) }),
    "hearts": () => P.push({ k: "heart", x: rand(0, W), y: H + rand(0, 40), vx: rand(-18, 18), vy: -rand(180, 340), s: rand(12, 26), sw: rand(0, 6.28), color: pick(["#ff375f", "#ff6b9d", "#ff2d92", "#ff8fab"]) }),
    "money-rain": () => P.push({ k: "money", x: rand(0, W), y: rand(-40, -6), vx: rand(-24, 24), vy: rand(240, 440), w: rand(24, 36), rot: rand(0, 6.28), vr: rand(-4, 4) }),
    "fireball": () => P.push({ k: "fire", x: rand(0, W), y: rand(-40, -6), vx: rand(-30, 30), vy: rand(360, 620), r: rand(10, 22), trail: [] }),
    "confetti": () => P.push({ k: "conf", x: rand(0, W), y: rand(-40, -6), vx: rand(-50, 50), vy: rand(260, 460), w: rand(5, 11), h: rand(8, 16), rot: rand(0, 6.28), vr: rand(-8, 8), color: pick(RAINBOW) }),
    "bubbles": () => P.push({ k: "bubble", x: rand(0, W), y: H + rand(0, 40), vx: rand(-14, 14), vy: -rand(150, 300), r: rand(7, 28), sw: rand(0, 6.28), hue: rand(180, 220) }),
    "lightning": () => P.push({ k: "rain", x: rand(0, W), y: rand(-20, -2), vx: rand(-30, -10), vy: rand(520, 820), len: rand(10, 22) }),
  };

  const burst = (cx, cy, colorFn, count, speed, gravity = 60) => {
    for (let i = 0; i < count; i++) {
      const a = rand(0, 6.2832), s = rand(speed * 0.25, speed);
      P.push({ k: "spark", x: cx, y: cy, vx: Math.cos(a) * s, vy: Math.sin(a) * s, r: rand(2, 5), color: colorFn(), decay: rand(0.45, 0.95), g: gravity, life: 1 });
    }
  };

  const perFrame = { "star-rain": 4, "snow": 5, "hearts": 3, "money-rain": 3, "fireball": 3, "confetti": 5, "bubbles": 3, "lightning": 6 };

  // lightning storm state
  const bolts = [];
  let nextBolt = start + 120;
  let flash = 0;
  const makeBolt = (x0) => {
    const pts = [{ x: x0, y: 0 }];
    let y = 0;
    const endY = H * rand(0.6, 0.95);
    while (y < endY) {
      y += rand(H * 0.05, H * 0.12);
      pts.push({ x: pts[pts.length - 1].x + rand(-40, 40), y: Math.min(y, endY) });
    }
    return { pts, life: 1 };
  };

  // one-shot burst effects
  if (effect === "star-explosion") burst(W / 2, H * 0.42, () => (Math.random() < 0.6 ? "#ffd60a" : "#fff3b0"), 140, 520, 40);
  if (effect === "rainbow-burst") burst(W / 2, H * 0.45, () => pick(RAINBOW), 160, 480, 30);

  const frame = (now) => {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    const t = now - start;
    ctx.clearRect(0, 0, W, H);

    // continuous spawning for rain-type effects
    if (t < duration * 0.72 && spawners[effect]) {
      for (let i = 0; i < (perFrame[effect] || 3); i++) spawners[effect]();
    }
    // fireworks: repeated bursts high on screen
    if (effect === "fireworks" && t < duration * 0.8 && now >= fireworkAt) {
      burst(rand(W * 0.15, W * 0.85), rand(H * 0.15, H * 0.5), () => pick(RAINBOW), 70, 320, 90);
      fireworkAt = now + rand(220, 420);
    }
    // secondary pops for the explosion effects
    if ((effect === "star-explosion" || effect === "rainbow-burst") && t < 700 && Math.random() < 0.12) {
      burst(rand(W * 0.3, W * 0.7), rand(H * 0.3, H * 0.6), () => (effect === "rainbow-burst" ? pick(RAINBOW) : "#ffd60a"), 40, 360, 40);
    }
    // lightning storm: schedule bolts + a screen flash
    if (effect === "lightning" && t < duration * 0.85 && now >= nextBolt) {
      bolts.push(makeBolt(rand(W * 0.2, W * 0.8)));
      flash = 0.85;
      nextBolt = now + rand(260, 700);
    }
    if (flash > 0) {
      ctx.fillStyle = `rgba(200,220,255,${flash * 0.5})`;
      ctx.fillRect(0, 0, W, H);
      flash -= dt * 3.5;
    }

    for (let i = P.length - 1; i >= 0; i--) {
      const p = P[i];
      if (p.k === "spark") {
        p.vy += p.g * dt;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.life -= p.decay * dt;
        if (p.life <= 0) { P.splice(i, 1); continue; }
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.shadowColor = p.color; ctx.shadowBlur = 10;
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.2832); ctx.fill();
        ctx.globalAlpha = 1; ctx.shadowBlur = 0;
        continue;
      }
      p.x += p.vx * dt; p.y += p.vy * dt;
      if (p.k === "star") {
        p.rot += p.vr * dt; p.tw += dt * 6;
        const a = 0.7 + 0.3 * Math.sin(p.tw);
        ctx.globalAlpha = a;
        drawStar(ctx, p.x, p.y, p.r, p.rot, p.gold ? "#ffd60a" : "#8fd3ff", p.gold ? "#ffb703" : "#4cc9f0");
        ctx.globalAlpha = 1; ctx.shadowBlur = 0;
      } else if (p.k === "snow") {
        p.sw += dt * 2; p.x += Math.sin(p.sw) * 12 * dt;
        ctx.fillStyle = "rgba(255,255,255,0.92)";
        ctx.shadowColor = "#cfe8ff"; ctx.shadowBlur = 6;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.2832); ctx.fill();
        ctx.shadowBlur = 0;
      } else if (p.k === "heart") {
        p.sw += dt * 2.5; p.x += Math.sin(p.sw) * 16 * dt;
        drawHeart(ctx, p.x, p.y, p.s, p.color);
        ctx.shadowBlur = 0;
      } else if (p.k === "money") {
        p.rot += p.vr * dt;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        const w = p.w, h = w * 0.5;
        ctx.fillStyle = "#3aa66b"; ctx.strokeStyle = "#1f7a4d"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.rect(-w / 2, -h / 2, w, h); ctx.fill(); ctx.stroke();
        ctx.fillStyle = "#eafff3"; ctx.font = `bold ${h * 0.7}px sans-serif`;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText("$", 0, 0);
        ctx.restore();
      } else if (p.k === "fire") {
        p.trail.push({ x: p.x, y: p.y }); if (p.trail.length > 6) p.trail.shift();
        for (let j = 0; j < p.trail.length; j++) {
          const tp = p.trail[j];
          drawFire(ctx, tp.x, tp.y, p.r * (0.4 + 0.1 * j));
        }
        drawFire(ctx, p.x, p.y, p.r);
      } else if (p.k === "conf") {
        p.rot += p.vr * dt;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * (0.5 + 0.5 * Math.abs(Math.sin(p.rot))));
        ctx.restore();
      } else if (p.k === "bubble") {
        p.sw += dt * 2; p.x += Math.sin(p.sw) * 14 * dt;
        ctx.save();
        const g = ctx.createRadialGradient(p.x - p.r * 0.3, p.y - p.r * 0.3, p.r * 0.1, p.x, p.y, p.r);
        g.addColorStop(0, "rgba(255,255,255,0.55)");
        g.addColorStop(0.5, `hsla(${p.hue},80%,85%,0.14)`);
        g.addColorStop(1, `hsla(${p.hue},80%,75%,0.06)`);
        ctx.fillStyle = g;
        ctx.strokeStyle = `hsla(${p.hue},80%,85%,0.55)`;
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.2832); ctx.fill(); ctx.stroke();
        ctx.fillStyle = "rgba(255,255,255,0.9)";
        ctx.beginPath(); ctx.arc(p.x - p.r * 0.32, p.y - p.r * 0.32, Math.max(1, p.r * 0.16), 0, 6.2832); ctx.fill();
        ctx.restore();
      } else if (p.k === "rain") {
        ctx.strokeStyle = "rgba(170,200,255,0.5)"; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x - p.vx * 0.02, p.y + p.len); ctx.stroke();
      }
      if (p.y > H + 60 || p.y < -120 || p.x < -80 || p.x > W + 80) P.splice(i, 1);
    }

    // draw lightning bolts on top
    for (let i = bolts.length - 1; i >= 0; i--) {
      const bo = bolts[i];
      ctx.save();
      ctx.globalAlpha = Math.max(0, bo.life);
      ctx.strokeStyle = "#eaf2ff";
      ctx.shadowColor = "#7db8ff";
      ctx.shadowBlur = 18;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(bo.pts[0].x, bo.pts[0].y);
      for (let j = 1; j < bo.pts.length; j++) ctx.lineTo(bo.pts[j].x, bo.pts[j].y);
      ctx.stroke();
      ctx.restore();
      bo.life -= dt * 4;
      if (bo.life <= 0) bolts.splice(i, 1);
    }

    if (!stopped && (t < duration + 1100)) {
      raf = requestAnimationFrame(frame);
    } else {
      cleanup();
    }
  };

  const cleanup = () => {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(raf);
    window.removeEventListener("resize", resize);
    try { ctx.clearRect(0, 0, W, H); } catch (_) {}
  };

  raf = requestAnimationFrame(frame);
  return cleanup;
}
